import { useAppStore } from '../stores/useAppStore';
import { headBlob, isSyncConfigured } from './syncClient';
import { prose as proseStore } from '../storage/db';
import type { AudioAsset, Story } from '../types';

function resolveVoice(story: Story): string {
  const universe = useAppStore.getState().universes[story.universeId];
  return (
    story.voiceIdOverride?.trim() ||
    universe?.defaultVoiceId?.trim() ||
    ''
  );
}

async function recoverAudio(story: Story): Promise<void> {
  const head = await headBlob(`audio_${story.id}`);
  if (!head) return;
  const asset: AudioAsset = {
    id: story.id,
    blobKey: story.id,
    mimeType: head.mime || 'audio/mpeg',
    voiceId: resolveVoice(story),
    createdAt: Date.now(),
  };
  const store = useAppStore.getState();
  store.setStoryAudio(story.id, asset);
  store.setInFlight(story.id, undefined);
  console.info('[recovery] audio recovered for', story.id);
}

async function recoverProse(story: Story): Promise<void> {
  const head = await headBlob(`prose_${story.id}`);
  if (!head) return;
  const text = await proseStore.get(story.id);
  if (!text) return;
  const store = useAppStore.getState();
  store.setStoryProseKey(story.id, story.id);
  store.setInFlight(story.id, undefined);
  console.info('[recovery] prose recovered for', story.id);
}

let recovering = false;

export async function recoverInFlight(): Promise<void> {
  if (!isSyncConfigured()) return;
  if (recovering) return;
  recovering = true;
  try {
    const stories = useAppStore.getState().stories;
    const tasks: Promise<unknown>[] = [];
    for (const story of Object.values(stories)) {
      if (story.inFlight === 'tts' && !story.audio) {
        tasks.push(recoverAudio(story).catch(() => {}));
      } else if (story.inFlight === 'prose' && !story.proseKey) {
        tasks.push(recoverProse(story).catch(() => {}));
      }
    }
    if (tasks.length === 0) return;
    await Promise.allSettled(tasks);
  } finally {
    recovering = false;
  }
}

export function installInFlightRecoveryHooks(): void {
  void recoverInFlight();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void recoverInFlight();
    }
  });
}
